import React, { useState, useMemo } from "react";
import Layout from "@/components/layout/Layout";
import { useGuestSaves } from "@/hooks/use-guest-saves";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, MapPin, Search, SortAsc, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { SEOHead } from "@/components/seo/SEOHead";
import { ExpertCallsSection } from "@/components/home/ExpertCallsSection";

export default function FavoritesPage() {
  const { guestSaves, removeSavedNursingHome, clearAllSaves } = useGuestSaves();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "name" | "county">("date");

  const filteredAndSortedSaves = useMemo(() => {
    let filtered = guestSaves.filter(save =>
      save.center_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      save.county_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.center_name.localeCompare(b.center_name);
        case "county":
          return a.county_name.localeCompare(b.county_name);
        case "date":
        default:
          return new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime();
      }
    });
  }, [guestSaves, searchTerm, sortBy]);

  const handleNursingHomeClick = (save: any) => {
    navigate(`/nursing-homes/${save.county_slug}/${save.nh_slug}`);
  };

  const handleRemove = (nhSlug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeSavedNursingHome(nhSlug);
  };

  if (guestSaves.length === 0) {
    return (
      <Layout>
        <SEOHead 
          title="Your Saved Nursing Homes | Grandpal"
          description="Manage your saved nursing homes and compare options."
          canonicalUrl="/favorites"
        />
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-16">
              <Heart className="h-16 w-16 mx-auto mb-6 text-gray-300" />
              <h1 className="text-3xl font-bold mb-4">No Saved Nursing Homes</h1>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Start exploring nursing homes and save your favorites to easily compare them later.
              </p>
              <Button 
                onClick={() => navigate("/nursing-homes")}
                className="bg-grandpal-green hover:bg-grandpal-green-dark"
              >
                Browse Nursing Homes
              </Button>
            </div>
          </div>
          
          {/* Expert Consultations Section */}
          <div className="my-16">
            <ExpertCallsSection />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead 
        title="Your Saved Nursing Homes | Grandpal"
        description="Manage your saved nursing homes and compare options."
        canonicalUrl="/favorites"
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Heart className="h-8 w-8 text-red-500 fill-current" />
              Your Saved Nursing Homes
            </h1>
            <p className="text-gray-600">
              Manage and compare your saved nursing homes ({guestSaves.length} saved)
            </p>
          </div>

          {/* Sign up prompt for guest users */}
          <Card className="mb-6 bg-grandpal-beige/30 border-grandpal-green/20">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-grandpal-green-dark mb-2">Create an account to sync your saves</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Sign up to access your saved nursing homes from any device and get personalized recommendations.
                  </p>
                  <Button 
                    size="sm" 
                    className="bg-grandpal-green hover:bg-grandpal-green-dark"
                    onClick={() => navigate("/auth/signup")}
                  >
                    Create Free Account
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by nursing home name or county..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={(value: "date" | "name" | "county") => setSortBy(value)}>
                <SelectTrigger className="w-40">
                  <SortAsc className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Recently Saved</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="county">County A-Z</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={clearAllSaves}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>
          </div>

          {/* Results */}
          {filteredAndSortedSaves.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No nursing homes match your search criteria.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredAndSortedSaves.map((save) => (
                <Card 
                  key={save.nh_slug} 
                  className="cursor-pointer hover:shadow-lg transition-shadow group"
                  onClick={() => handleNursingHomeClick(save)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg text-grandpal-green-dark group-hover:text-grandpal-green truncate">
                          {save.center_name}
                        </h3>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{save.county_name}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleRemove(save.nh_slug, e)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      >
                        <Heart className="h-4 w-4 fill-current" />
                      </Button>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-xs">
                        Saved {formatDistanceToNow(new Date(save.savedAt))} ago
                      </Badge>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-grandpal-green transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Expert Consultations Section */}
          <div className="my-16">
            <ExpertCallsSection />
          </div>

          {/* Footer Actions */}
          {filteredAndSortedSaves.length > 0 && (
            <div className="flex justify-center mt-8">
              <Button 
                variant="outline"
                onClick={() => navigate("/nursing-homes")}
                className="border-grandpal-green text-grandpal-green hover:bg-grandpal-green hover:text-white"
              >
                Browse More Nursing Homes
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}